import {
    type MetricSeriesConfig,
    type RangeKey,
    useMetricChart,
    type XAxisType,
} from '@hooks/graph/useMetricChart';
import React, { useState, useEffect, useRef } from 'react';
import { ChatCircleDots } from '@phosphor-icons/react';
import {
    Area,
    Bar,
    CartesianGrid,
    Cell,
    ComposedChart,
    Label,
    Line,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { formatCount, formatCompact } from '@utils/currency';

const DEFAULT_COLORS = [
    '#2563eb',
    '#16a34a',
    '#9333ea',
    '#f97316',
    '#e11d48',
    '#0f766e',
];

export type ChartKind =
    | 'line'
    | 'bar'
    | 'area'
    | 'mixed' // per-series renderAs se decide hoga
    | 'pie'
    | 'doughnut';

export interface ChartControlsConfig {
    showRange?: boolean; // date range pills
    showSeriesToggles?: boolean; // legend toggles
    showToolbar?: boolean; // title + subtitle bar
}

export interface GenericChartCardProps<TData extends object> {
    kind: ChartKind;

    title?: string;
    subtitle?: string;

    data: TData[];
    dimensionKey: keyof TData;
    dimensionType?: XAxisType;

    /** metrics / series */
    series: MetricSeriesConfig<TData>[];

    /** for pie/doughnut -> which metric to use */
    pieMetricId?: string;

    /** chart height */
    height?: number;

    /** axis labels (Cartesian only) */
    xLabel?: string;
    yLabel?: string;

    /** filters */
    controls?: ChartControlsConfig;
    initialRange?: RangeKey;

    /** extra actions in header */
    rightSlot?: React.ReactNode;

    /** When provided, clicking the chart title adds this widget to the AI chat. */
    onAddToChat?: () => void;

    /**
     * Makes the plotted points selectable, handing back the row that was clicked.
     *
     * A chart of days is also the most natural date picker a screen can offer — clicking the
     * bar you are already looking at beats finding a separate control for the same thing. Only
     * bars are wired: a line or an area has no obvious per-point hit area, and a click that
     * works on some chart kinds and silently does nothing on others is worse than no click.
     */
    onPointClick?: (row: TData, index: number) => void;

    /** Index of the point to draw as selected. Ignored without `onPointClick`. */
    selectedIndex?: number | null;

    /** wrapper class */
    className?: string;

    loading?: boolean;
}

/**
 * ONE generic, mobile responsive chart card
 *  - uses useMetricChart for all calculations & filters
 *  - supports line / bar / area / mixed / pie / doughnut
 */
export function GenericChartCard<TData extends object>(
    props: GenericChartCardProps<TData>,
) {
    const {
        kind,
        title,
        subtitle,
        data,
        dimensionKey,
        dimensionType = 'date',
        series,
        pieMetricId,
        height = 260,
        xLabel,
        yLabel,
        controls,
        initialRange = 'all',
        rightSlot,
        onAddToChat,
        onPointClick,
        selectedIndex,
        className,
        loading = false,
    } = props;

    const {
        chartData,
        seriesWithState,
        toggleSeries,
        range,
        setRange,
        dimensionType: resolvedDimType,
    } = useMetricChart<TData>({
        data,
        dimensionKey,
        dimensionType,
        series,
        enableRangeFilter: controls?.showRange,
        initialRange,
    });

    const activeSeries = React.useMemo(
        () => seriesWithState.filter((s) => s.active),
        [seriesWithState],
    );

    const yDomain = React.useMemo<[number, number]>(() => {
        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;

        chartData.forEach((row) => {
            activeSeries.forEach((s) => {
                const val = row[s.id as keyof typeof row];
                if (typeof val === 'number') {
                    min = Math.min(min, val);
                    max = Math.max(max, val);
                }
            });
        });

        if (!Number.isFinite(min) || !Number.isFinite(max)) {
            return [0, 1];
        }

        if (min === max) {
            const padding = min === 0 ? 1 : Math.abs(min) * 0.2;
            return [Math.max(0, min - padding), max + padding];
        }

        const lower = Math.min(0, min);
        const upperPadding = Math.max(1, Math.abs(max) * 0.05);
        return [lower, max + upperPadding];
    }, [chartData, activeSeries]);

    const showToolbar = controls?.showToolbar ?? true;

    // Selection state for Add to Chat affordance
    const [isSelected, setIsSelected] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isSelected || !onAddToChat) return;
        const handleOutsideClick = (e: MouseEvent) => {
            if (
                cardRef.current &&
                !cardRef.current.contains(e.target as Node)
            ) {
                setIsSelected(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () =>
            document.removeEventListener('mousedown', handleOutsideClick);
    }, [isSelected, onAddToChat]);
    const showRange = controls?.showRange ?? true;
    const showSeriesToggles = controls?.showSeriesToggles ?? true;

    const formatDate = (value: any) =>
        new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        });

    const rangeOptions: RangeKey[] = ['7d', '30d', '90d', 'all'];

    // PIE / DOUGHNUT specific data
    const pieSeries =
        seriesWithState.find((s) => s.id === pieMetricId) ?? seriesWithState[0];

    const pieData =
        kind === 'pie' || kind === 'doughnut'
            ? chartData.map((d) => ({
                  name: d.x,
                  value: (pieSeries ? d[pieSeries.id] : null) as number | null,
              }))
            : [];

    const isRadial = kind === 'pie' || kind === 'doughnut';
    const isCartesian = !isRadial;

    return (
        <div
            ref={cardRef}
            className={
                'w-full rounded-3xl border bg-slate-50 px-4 py-4 shadow-sm sm:px-6 sm:py-5 ' +
                (isSelected && onAddToChat
                    ? 'border-indigo-400 '
                    : 'border-slate-200 ') +
                (className ?? '')
            }
            style={
                onAddToChat
                    ? {
                          position: 'relative',
                          cursor: 'pointer',
                          outline: isSelected
                              ? '2px solid rgba(99,102,241,0.25)'
                              : 'none',
                          outlineOffset: 2,
                          transition:
                              'border-color 0.15s ease, outline 0.15s ease',
                      }
                    : undefined
            }
            onClick={onAddToChat ? () => setIsSelected((v) => !v) : undefined}
        >
            {/* Add to Chat chip — floats above the top-right corner of the selection ring */}
            {isSelected && onAddToChat && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onAddToChat();
                        setIsSelected(false);
                    }}
                    style={{
                        position: 'absolute',
                        top: -24,
                        right: 0,
                        zIndex: 20,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        padding: '2px 7px',
                        fontSize: 10,
                        fontWeight: 500,
                        color: '#6366f1',
                        background: 'rgba(99,102,241,0.06)',
                        border: '1px solid rgba(99,102,241,0.22)',
                        borderRadius: 5,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 4px rgba(99,102,241,0.12)',
                        lineHeight: 1.4,
                    }}
                >
                    <ChatCircleDots size={10} weight="duotone" />
                    Add to Chat
                </button>
            )}

            {/* ───────── Header / toolbar ───────── */}
            {showToolbar && (title || subtitle || rightSlot) && (
                <div className="mb-3 flex flex-col gap-2 sm:mb-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex flex-col gap-0">
                        {title && (
                            <h2 className="text-sm font-semibold text-slate-900 sm:text-base">
                                {title}
                            </h2>
                        )}
                        {subtitle && (
                            <p className="mt-0.5 text-[11px] text-slate-500 sm:text-xs">
                                {subtitle}
                            </p>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        {/* Date range pills sirf cartesian + date dims pe */}
                        {isCartesian &&
                            resolvedDimType === 'date' &&
                            showRange && (
                                <div className="flex rounded-full bg-white p-1 text-[10px] shadow-sm sm:text-xs">
                                    {rangeOptions.map((opt) => (
                                        <button
                                            key={opt}
                                            type="button"
                                            onClick={() => setRange(opt)}
                                            className={[
                                                'rounded-full px-2 py-1 transition-colors',
                                                range === opt
                                                    ? 'bg-slate-900 text-white'
                                                    : 'text-slate-600 hover:bg-slate-100',
                                            ].join(' ')}
                                        >
                                            {opt === 'all'
                                                ? 'All'
                                                : opt.replace('d', ' days')}
                                        </button>
                                    ))}
                                </div>
                            )}

                        {rightSlot}
                    </div>
                </div>
            )}

            {/* ───────── Series toggles / legend ───────── */}
            {showSeriesToggles && seriesWithState.length > 1 && (
                <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] sm:text-xs">
                    {seriesWithState.map((s, index) => {
                        const color =
                            s.color ??
                            DEFAULT_COLORS[index % DEFAULT_COLORS.length];
                        const active = s.active;

                        return (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => toggleSeries(s.id)}
                                className={[
                                    'inline-flex items-center gap-2 rounded-full border px-2 py-1 transition-colors',
                                    active
                                        ? 'border-slate-200 bg-white text-slate-800'
                                        : 'border-slate-100 bg-slate-100 text-slate-400',
                                ].join(' ')}
                            >
                                <span
                                    className="h-2 w-2 rounded-full"
                                    style={{ backgroundColor: color }}
                                />
                                <span>{s.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* ───────── Main chart area ───────── */}
            {/*
              * The box follows the height prop. It used to be a fixed 220/260 class while the
              * ResponsiveContainer inside used the prop, so asking for a shorter chart left the
              * difference as dead space under the axis instead of shrinking the card.
              */}
            <div className="w-full" style={{ height }}>
                <ResponsiveContainer width="100%" height={height}>
                    {/* CARTESIAN (line / bar / area / mixed) */}
                    {isCartesian && (
                        <ComposedChart
                            data={chartData}
                            margin={{ top: 8, right: 24, bottom: 8, left: 16 }}
                        >
                            <CartesianGrid
                                stroke="#e5e7eb"
                                strokeDasharray="3 3"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="x"
                                tickMargin={8}
                                tickLine={false}
                                axisLine={{ stroke: '#e5e7eb' }}
                                tickFormatter={
                                    resolvedDimType === 'date'
                                        ? formatDate
                                        : (v) => v
                                }
                                style={{ fontSize: 11 }}
                            >
                                {xLabel && (
                                    <Label
                                        value={xLabel}
                                        position="insideBottom"
                                        dy={12}
                                        style={{
                                            fontSize: 11,
                                            fill: '#6b7280',
                                        }}
                                    />
                                )}
                            </XAxis>
                            <YAxis
                                tickLine={false}
                                axisLine={{ stroke: '#e5e7eb' }}
                                tickMargin={8}
                                width={60}
                                style={{ fontSize: 11 }}
                                domain={yDomain}
                                tickFormatter={(v) => {
                                    if (typeof v !== 'number') return v;
                                    const abs = Math.abs(v);
                                    if (abs >= 1000) {
                                        return formatCompact(v);
                                    }
                                    return formatCount(v);
                                }}
                            >
                                {yLabel && (
                                    <Label
                                        value={yLabel}
                                        angle={-90}
                                        position="insideLeft"
                                        dx={-8}
                                        style={{
                                            fontSize: 11,
                                            fill: '#6b7280',
                                        }}
                                    />
                                )}
                            </YAxis>
                            <Tooltip
                                cursor={{ stroke: '#cbd5f5', strokeWidth: 1 }}
                                formatter={(value: any, name: string) => {
                                    const s = seriesWithState.find(
                                        (x) => x.id === name,
                                    );
                                    const label = s?.label ?? name;
                                    if (typeof value === 'number') {
                                        return [value.toFixed(2), label];
                                    }
                                    return [value, label];
                                }}
                                labelFormatter={(label: any) =>
                                    resolvedDimType === 'date'
                                        ? formatDate(label)
                                        : label
                                }
                                wrapperStyle={{ fontSize: 11 }}
                            />

                            {seriesWithState.map((s, index) => {
                                if (!s.active) return null;

                                const color =
                                    s.color ??
                                    DEFAULT_COLORS[
                                        index % DEFAULT_COLORS.length
                                    ];
                                const renderAs =
                                    kind === 'mixed'
                                        ? (s.renderAs ?? 'line')
                                        : kind; // line/bar/area

                                if (renderAs === 'bar') {
                                    return (
                                        <Bar
                                            key={s.id}
                                            dataKey={s.id}
                                            name={s.label}
                                            fill={color}
                                            radius={[4, 4, 0, 0]}
                                            barSize={18}
                                            isAnimationActive={false}
                                            cursor={onPointClick ? 'pointer' : undefined}
                                            onClick={
                                                onPointClick
                                                    ? (_entry: unknown, index: number) =>
                                                          onPointClick(
                                                              chartData[index] as TData,
                                                              index,
                                                          )
                                                    : undefined
                                            }
                                        >
                                            {/*
                                              * Selection is drawn by dimming everything else
                                              * rather than recolouring the chosen bar, so the
                                              * series keeps one colour and the eye still reads
                                              * the shape of the whole window.
                                              */}
                                            {onPointClick &&
                                                chartData.map((row, index) => (
                                                    <Cell
                                                        key={`${s.id}-${index}`}
                                                        fill={color}
                                                        fillOpacity={
                                                            selectedIndex == null ||
                                                            selectedIndex === index
                                                                ? 1
                                                                : 0.35
                                                        }
                                                    />
                                                ))}
                                        </Bar>
                                    );
                                }

                                if (renderAs === 'area') {
                                    return (
                                        <Area
                                            key={s.id}
                                            type="monotone"
                                            dataKey={s.id}
                                            name={s.label}
                                            stroke={color}
                                            fill={color + '33'}
                                            strokeWidth={s.strokeWidth ?? 2}
                                            isAnimationActive={false}
                                        />
                                    );
                                }

                                // default: line
                                return (
                                    <Line
                                        key={s.id}
                                        type="monotone"
                                        dataKey={s.id}
                                        name={s.label}
                                        stroke={color}
                                        strokeWidth={s.strokeWidth ?? 2}
                                        dot={{ r: 3 }}
                                        activeDot={{ r: 5 }}
                                        strokeDasharray={
                                            s.variant === 'dashed'
                                                ? '4 4'
                                                : undefined
                                        }
                                        isAnimationActive={false}
                                    />
                                );
                            })}
                        </ComposedChart>
                    )}

                    {/* RADIAL (pie / doughnut) */}
                    {isRadial && (
                        <PieChart>
                            <Tooltip
                                formatter={(value: any) =>
                                    typeof value === 'number'
                                        ? value.toFixed(2)
                                        : value
                                }
                                wrapperStyle={{ fontSize: 11 }}
                            />
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                outerRadius="80%"
                                innerRadius={
                                    kind === 'doughnut' ? '55%' : undefined
                                }
                                paddingAngle={2}
                                isAnimationActive={false}
                            >
                                {pieData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={
                                            DEFAULT_COLORS[
                                                index % DEFAULT_COLORS.length
                                            ]
                                        }
                                    />
                                ))}
                            </Pie>
                        </PieChart>
                    )}
                </ResponsiveContainer>
            </div>
        </div>
    );
}
